import { useEffect, useRef, useState } from 'react'

import {
  Drawer,
  Header,
  type InitialUploadFile,
  PrimaryButton,
  SingleFileViewerUpload,
  Space,
  TertiaryButton,
  type UploadFile,
} from '@procurify/ui'

import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import {
  EditExpenseLineItemPresenter,
  type EditExpenseLineItemFormHandle,
} from '@webapp/modules/expense/features/edit-expense-line-item'
import { EditExpenseLineItemDrawerLoader } from './EditExpenseLineItemDrawerLoader'
import messages from './messages'
import { EditExpenseLineItemDrawerContentStyled } from './styles/EditExpenseLineItemDrawerContentStyled'
import { EditExpenseLineItemDrawerDocumentStyled } from './styles/EditExpenseLineItemDrawerDocumentStyled'
import { EditExpenseLineItemDrawerFooterStyled } from './styles/EditExpenseLineItemDrawerFooterStyled'
import { useEditExpenseLineItemDrawer } from './useEditExpenseLineItemDrawer'

export interface IEditExpenseLineItemDrawerProps {
  drawerOpen: boolean
  onClose: () => void
  item: ExpenseItem
  expenseReportId: number
  requesterId: number
  secondaryCategoryId: number | undefined
  onSuccess: () => void
  canOverrideTaxAmount?: boolean
}

export const EditExpenseLineItemDrawer = ({
  drawerOpen,
  onClose,
  item,
  expenseReportId,
  requesterId,
  secondaryCategoryId,
  onSuccess,
  canOverrideTaxAmount = false,
}: IEditExpenseLineItemDrawerProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()
  const formRef = useRef<EditExpenseLineItemFormHandle | null>(null)

  const [currentAttachment, setCurrentAttachment] = useState<string>(
    item.attachment ?? ''
  )

  useEffect(() => {
    setCurrentAttachment(item.attachment ?? '')
  }, [item])

  const {
    data: { reimbursementSetting, mileageLockSetting, mileageConfiguration },
    states: { isFormDataLoading },
  } = useEditExpenseLineItemDrawer({ requesterId })

  const handleUploadComplete = (file: UploadFile) => {
    // Use the server-returned file path ("attachments/<uuid>") so Django
    // FieldFile.url can construct a valid S3 URL on server-side PDF/export
    // paths. Persisting a bare UUID reads back fine in-app (via the
    // /attachments/{uuid}/ resolver) but breaks PO / Remittance rendering.
    // Fail visibly when the response omits .file rather than falling back to
    // the UUID. See PLAT-4061 / PR #9895.
    const filePath = file.rawResponse?.data?.file ?? file.file ?? ''
    setCurrentAttachment(String(filePath))
  }

  const handleDeleteFile = () => {
    setCurrentAttachment('')
  }

  const initialFile: InitialUploadFile | undefined = currentAttachment
    ? {
        id: currentAttachment,
        file: formatMedia(currentAttachment),
      }
    : undefined

  const renderDocument = () => (
    <EditExpenseLineItemDrawerDocumentStyled>
      <SingleFileViewerUpload
        initialFile={initialFile}
        onUploadComplete={handleUploadComplete}
        onDelete={handleDeleteFile}
        disabled={false}
      />
    </EditExpenseLineItemDrawerDocumentStyled>
  )

  return (
    <Drawer
      drawerOpen={drawerOpen}
      setDrawerOpen={onClose}
      documentOpen={true}
      DocumentProps={{
        renderDocument,
        closeDrawerOnEsc: true,
      }}
      DrawerProps={{
        renderTitle: () => (
          <Header as='h4'>{intl.formatMessage(messages.drawerTitle)}</Header>
        ),
        renderFooter: () => (
          <EditExpenseLineItemDrawerFooterStyled>
            <Space direction='horizontal' justify='start' align='center'>
              <PrimaryButton
                type='button'
                onClick={() => formRef.current?.submitForm()}
              >
                {intl.formatMessage(messages.saveButton)}
              </PrimaryButton>
              <TertiaryButton type='button' onClick={onClose}>
                {intl.formatMessage(messages.cancelButton)}
              </TertiaryButton>
            </Space>
          </EditExpenseLineItemDrawerFooterStyled>
        ),
      }}
    >
      {() =>
        isFormDataLoading ? (
          <EditExpenseLineItemDrawerContentStyled>
            <EditExpenseLineItemDrawerLoader />
          </EditExpenseLineItemDrawerContentStyled>
        ) : (
          <EditExpenseLineItemDrawerContentStyled>
            <EditExpenseLineItemPresenter
              item={item}
              expenseReportId={expenseReportId}
              requesterId={requesterId}
              secondaryCategoryId={secondaryCategoryId}
              reimbursementSetting={reimbursementSetting}
              mileageLockSetting={mileageLockSetting}
              mileageConfiguration={mileageConfiguration}
              attachment={currentAttachment}
              onSuccess={onSuccess}
              formRef={formRef}
              canOverrideTaxAmount={canOverrideTaxAmount}
            />
          </EditExpenseLineItemDrawerContentStyled>
        )
      }
    </Drawer>
  )
}

EditExpenseLineItemDrawer.displayName = 'EditExpenseLineItemDrawer'
